import axios, { AxiosRequestConfig } from 'axios';
import { HOST_API } from 'src/config-global';
import { getAuthToken, setAuthToken, getRefreshToken, setRefreshToken } from './cookies';

// ----------------------------------------------------------------------

// Ensure baseURL doesn't end with slash to avoid double slash issues
const baseURL = HOST_API?.endsWith('/') ? HOST_API.slice(0, -1) : HOST_API;
const axiosInstance = axios.create({ baseURL });

// Add token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const refreshToken = getRefreshToken();

    // Enhanced request logging especially for garbage type endpoints
    const isGarbageTypeEndpoint = config.url?.includes('garbage-types');
    const isCloneEndpoint = config.url?.includes('clone');

    if (isGarbageTypeEndpoint || isCloneEndpoint) {
      console.group('🔑 AXIOS INTERCEPTOR - ENHANCED REQUEST DEBUG');
      console.log('📡 Request Details:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
        timestamp: new Date().toISOString(),
      });

      console.log('🔐 Token Status:', {
        hasAuthToken: !!token,
        tokenLength: token?.length || 0,
        tokenPreview: token ? `${token.substring(0, 20)}...${token.slice(-5)}` : 'none',
        hasRefreshToken: !!refreshToken,
        refreshTokenLength: refreshToken?.length || 0,
      });

      console.log('📋 Headers Being Sent:', {
        authorization: config.headers.Authorization || 'Not set',
        contentType: config.headers['Content-Type'] || 'Not set',
        allHeaders: Object.keys(config.headers),
      });

      if (config.data) {
        console.log('📦 Request Data:', config.data);
      }

      console.groupEnd();
    } else {
      console.log('🔑 Axios Interceptor: Token check', {
        hasToken: !!token,
        url: config.url,
        method: config.method?.toUpperCase(),
        tokenPreview: token ? `${token.substring(0, 20)}...${token.slice(-5)}` : 'none',
      });
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️ No auth token available for request to:', config.url);
    }

    return config;
  },
  (error) => {
    console.error('❌ Axios Request Interceptor Error:', error);
    return Promise.reject(error);
  }
);

// Track if we're already redirecting to prevent multiple redirects
let isRedirecting = false;

// Handle token refresh on 401 responses
axiosInstance.interceptors.response.use(
  (res) => {
    // Enhanced success response logging for garbage type endpoints
    const isGarbageTypeEndpoint = res.config.url?.includes('garbage-types');
    const isCloneEndpoint = res.config.url?.includes('clone');

    if (isGarbageTypeEndpoint || isCloneEndpoint) {
      console.log('✅ AXIOS INTERCEPTOR - SUCCESSFUL RESPONSE:', {
        method: res.config.method?.toUpperCase(),
        url: res.config.url,
        status: res.status,
        statusText: res.statusText,
        dataSize: JSON.stringify(res.data).length,
        timestamp: new Date().toISOString(),
      });
    }

    return res;
  },
  async (error) => {
    const originalRequest = error.config;
    const isGarbageTypeEndpoint = originalRequest?.url?.includes('garbage-types');
    const isCloneEndpoint = originalRequest?.url?.includes('clone');

    // Enhanced error logging for garbage type endpoints
    if (isGarbageTypeEndpoint || isCloneEndpoint) {
      console.group('❌ AXIOS INTERCEPTOR - ERROR RESPONSE DEBUG');
      console.error('📡 Failed Request Details:', {
        method: originalRequest?.method?.toUpperCase(),
        url: originalRequest?.url,
        fullURL: `${originalRequest?.baseURL}${originalRequest?.url}`,
        timestamp: new Date().toISOString(),
      });

      if (error.response) {
        console.error('🌐 Error Response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers,
        });

        // Special handling for 403 Forbidden
        if (error.response.status === 403) {
          console.error('🚫 403 FORBIDDEN ANALYSIS:');
          console.error('   • Request was authenticated but not authorized');
          console.error('   • Check user permissions for this organization');
          console.error('   • Verify user role has required access level');
          console.error("   • Ensure organizeId matches user's access");

          const token = getAuthToken();
          if (token) {
            console.error('🔍 Current Token Analysis:', {
              tokenLength: token.length,
              tokenFormat: token.includes('.') ? 'JWT Format' : 'Unknown Format',
              tokenParts: token.split('.').length,
            });
          }
        }
      }

      console.groupEnd();
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          console.log('🔄 Axios interceptor: Attempting token refresh...');
          const response = await axios.post(`${HOST_API}/auth/refresh-token`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;

          setAuthToken(accessToken);
          if (newRefreshToken) {
            setRefreshToken(newRefreshToken);
          }

          console.log('✅ Axios interceptor: Token refreshed successfully');
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return await axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.log('❌ Axios interceptor: Token refresh failed');
        // Refresh failed, redirect to login only if not already redirecting
        if (!isRedirecting) {
          isRedirecting = true;
          // Add small delay to prevent race conditions
          setTimeout(() => {
            window.location.href = '/auth/login';
          }, 100);
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    me: '/users/me/profile',
    login: '/auth/admin/signin-email',
    refreshToken: '/auth/refresh-token',
    generateOTP: '/auth/generate-otp',
    verifyOTP: '/auth/verify-otp',
    resetPassword: '/auth/reset-password',
    newPassword: '/auth/new-password',
    forgotPassword: '/auth/forgot-password',
    confirmRegister: '/auth/confirm-register',
    resendCodeRegister: '/auth/resend-code-register',
  },

  reports: {
    activeLocation: '/reports/active-location', // ดึงรายงานจำนวน Location ที่ Active อยู่
    garbageCollection: '/reports/garbage-collection', // ดึงรายงานปริมาณขยะ
    garbageByLocation: '/reports/garbage-by-location', // ดึงรายงานปริมาณขยะแยกตาม Location
    garbageByManager: '/reports/garbage-by-manager', // ดึงรายงานปริมาณขยะแยกตาม Manager
    garbageByUser: '/reports/garbage-by-user', // ดึงรายงานปริมาณขยะแยกตาม (ผู้ใช้งาน)
    garbageByType: '/reports/garbage-by-type', // ดึงรายงานปริมาณขยะแยกตามประเภทขยะ
    activeUser: '/reports/active-user', // ดึงรายงานตามสัดส่วน User ที่ Active อยู่
    carbonReduction: '/reports/carbon-reduction', // ดึงรายงานปริมาณการลด Carbon footprint ของขยะรีไซเคิล
    carbonEmission: '/reports/carbon-emission', // ดึงรายงานปริมาณการปล่อย Carbon footprint ของขยะรีไซเคิลหลังนำไปกำจัด
    garbageCollectionLog: '/reports/garbage-collection-log', // ดึงรายงานการรวบรวมขยะ (ข้อมูลดิบ)
    order: '/reports/order', // ดึงรายงานการออเดอร์ของขยะ (ข้อมูลดิบ)
    redeamLog: '/reports/redeam-log', // ดึงรายงานการแลกของรางวัล
  },

  users: {
    get: '/users',
    create: '/users',
    getById: (id: string) => `/users/${id}`,
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`,
    setRole: '/users/set-role',
    role: {
      switchCurrentRole: (id: string) => `/admin/user-roles/switch-role/current/${id}`,
      setCurrentRole: (id: string) => `/admin/user-roles/switch-role/${id}`,
      setRole: '/admin/user-roles',
      updateRole: (id: string) => `/admin/user-roles/${id}`,
      deleteRole: (id: string) => `/admin/user-roles/${id}`,
    },
  },

  userContact: {
    get: '/user-contacts',
    create: '/user-contacts',
    getById: (id: string) => `/user-contacts/${id}`,
    update: (id: string) => `/user-contacts/${id}`,
    delete: (id: string) => `/user-contacts/${id}`,
  },

  nodes: {
    get: '/admin/nodes',
    create: '/admin/nodes',
    getById: (id: string) => `/admin/nodes/${id}`,
    update: (id: string) => `/admin/nodes/${id}`,
    delete: (id: string) => `/admin/nodes/${id}`,
  },

  organize: {
    get: '/admin/organizes',
    create: '/admin/organizes',
    getById: (id: string) => `/admin/organizes/${id}`,
    update: (id: string) => `/admin/organizes/${id}`,
    delete: (id: string) => `/admin/organizes/${id}`,
    garbageConfig: (id: string) => `/admin/organizes/${id}/garbage-config`,
  },

  article: {
    category: {
      get: '/admin/article-categories',
      create: '/admin/article-categories',
      getById: (id: string) => `/admin/article-categories/${id}`,
      update: (id: string) => `/admin/article-categories/${id}`,
      delete: (id: string) => `/admin/article-categories/${id}`,
    },
    get: '/admin/articles',
    create: '/admin/articles',
    getById: (id: string) => `/admin/articles/${id}`,
    update: (id: string) => `/admin/articles/${id}`,
    delete: (id: string) => `/admin/articles/${id}`,
  },

  token: {
    get: '/admin/tokens',
    create: '/admin/tokens',
    getById: (id: string) => `/admin/tokens/${id}`,
    update: (id: string) => `/admin/tokens/${id}`,
    delete: (id: string) => `/admin/tokens/${id}`,
  },

  order: {
    get: '/admin/orders',
    delete: (id: string) => `/admin/orders/${id}`,
  },

  quest: {
    get: '/admin/quests',
    create: '/admin/quests',
    getById: (id: string) => `/admin/quests/${id}`,
    update: (id: string) => `/admin/quests/${id}`,
    delete: (id: string) => `/admin/quests/${id}`,
  },

  questReward: {
    get: '/admin/quest-rewards',
    create: '/admin/quest-rewards',
    getById: (id: string) => `/admin/quest-rewards/${id}`,
    update: (id: string) => `/admin/quest-rewards/${id}`,
    delete: (id: string) => `/admin/quest-rewards/${id}`,
  },

  reward: {
    get: '/rewards',
    create: '/rewards',
    getById: (id: string) => `/rewards/${id}`,
    update: (id: string) => `/rewards/${id}`,
    delete: (id: string) => `/rewards/${id}`,
  },

  redeam: {
    get: '/redeam-logs',
    create: '/redeam-logs',
    getById: (id: string) => `/redeam-logs/${id}`,
    confirm: (id: string) => `/redeam-logs/confirm/${id}`,
    cancel: (id: string) => `/redeam-logs/cancel/${id}`,
  },

  garbage: {
    get: '/admin/garbages',
    create: '/admin/garbages',
    getById: (id: string) => `/admin/garbages/${id}`,
    update: (id: string) => `/admin/garbages/${id}`,
    delete: (id: string) => `/admin/garbages/${id}`,
    type: {
      get: '/admin/garbage-types',
      admin: '/admin/garbage-types',
      global: '/admin/garbage-types/global',
      create: '/admin/garbage-types',
      getById: (id: string) => `/admin/garbage-types/${id}`,
      update: (id: string) => `/admin/garbage-types/${id}`,
      delete: (id: string) => `/admin/garbage-types/${id}`,
      clone: {
        bulk: '/admin/garbage-types/clone/bulk',
        specific: '/admin/garbage-types/clone/specific',
        available: (organizeId: string) => `/admin/garbage-types/clone/available/${organizeId}`,
      },
    },
  },

  garbageCollection: {
    log: {
      get: '/admin/garbage-collection-logs',
      create: '/admin/garbage-collection-logs',
      getById: (id: string) => `/admin/garbage-collection-logs/${id}`,
      update: (id: string) => `/admin/garbage-collection-logs/${id}`,
      delete: (id: string) => `/admin/garbage-collection-logs/${id}`,
    },
    manageBy: {
      get: '/admin/garbage-collection-manages-by',
      create: '/admin/garbage-collection-manages-by',
      getById: (id: string) => `/admin/garbage-collection-manages-by/${id}`,
      update: (id: string) => `/admin/garbage-collection-manages-by/${id}`,
      delete: (id: string) => `/admin/garbage-collection-manages-by/${id}`,
    },
    manageType: {
      get: '/admin/garbage-collection-manage-types',
      create: '/admin/garbage-collection-manage-types',
      getById: (id: string) => `/admin/garbage-collection-manage-types/${id}`,
      update: (id: string) => `/admin/garbage-collection-manage-types/${id}`,
      delete: (id: string) => `/admin/garbage-collection-manage-types/${id}`,
    },
    location: {
      get: '/admin/locations',
      create: '/admin/locations',
      getById: (id: string) => `/admin/locations/${id}`,
      update: (id: string) => `/admin/locations/${id}`,
      delete: (id: string) => `/admin/locations/${id}`,
    },
    locationType: {
      get: '/admin/location-types',
      create: '/admin/location-types',
      getById: (id: string) => `/admin/location-types/${id}`,
      update: (id: string) => `/admin/location-types/${id}`,
      delete: (id: string) => `/admin/location-types/${id}`,
    },
  },

  theme: {
    get: '/themes',
    create: '/themes',
    getById: (id: string) => `/themes/${id}`,
    update: (id: string) => `/themes/${id}`,
    delete: (id: string) => `/themes/${id}`,
  },

  banner: {
    get: '/banners',
    create: '/banners',
    getById: (id: string) => `/banners/${id}`,
    update: (id: string) => `/banners/${id}`,
    delete: (id: string) => `/banners/${id}`,
  },

  invitationCode: {
    get: '/invitation-codes',
    create: '/invitation-codes',
    update: (id: string) => `/invitation-codes/${id}`,
    logs: {
      get: '/invitation-code-logs/',
    },
  },
};
